import { doGithubRechallengeAction } from '@/actions/auth'
import { STORAGE_REFRESH_TOKEN_KEY, STORAGE_TOKEN_KEY } from '@/constants'

import { updateLocalToken } from './token'

export type CommonErrorObject = {
  code: number
  error: string
}

export class CommonError extends Error {
  info: CommonErrorObject

  constructor(info: CommonErrorObject, errorOptions?: ErrorOptions) {
    const message = JSON.stringify(info)
    super(message, errorOptions)
    this.info = info
  }
}

export async function handleRechallenge() {
  // Only rechallenge in the browser
  if (typeof window === 'undefined') {
    throw new Error('Not in browser')
  }
  const refreshToken = localStorage.getItem(STORAGE_REFRESH_TOKEN_KEY)
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    const result = await doGithubRechallengeAction(refreshToken)
    // Update tokens locally
    updateLocalToken(result.token)
    localStorage.setItem(STORAGE_TOKEN_KEY, result.token)
    localStorage.setItem(STORAGE_REFRESH_TOKEN_KEY, result.refreshToken)
    return result.token
  } catch (error) {
    // If rechallenge fails, clear tokens and throw error
    localStorage.removeItem(STORAGE_TOKEN_KEY)
    localStorage.removeItem(STORAGE_REFRESH_TOKEN_KEY)
    // Redirect to login
    location.replace('/auth/github')
    throw error
  }
}

export async function makeRequestWithRechallenge<T>(
  makeRequest: (token?: string) => Promise<T>,
  retryCount = 0
): Promise<T> {
  const MAX_RETRIES = 3

  try {
    return await makeRequest()
  } catch (e) {
    if (
      e instanceof CommonError &&
      e.info.code === 401 &&
      typeof window !== 'undefined'
    ) {
      // Check retry limit
      if (retryCount >= MAX_RETRIES) {
        console.warn('Max retry attempts reached for rechallenge')
        throw e
      }

      // Try rechallenge flow
      try {
        const newToken = await handleRechallenge()
        // Retry the original request with new token and increment retry count
        return makeRequestWithRechallenge(
          (token) => makeRequest(token || newToken),
          retryCount + 1
        )
      } catch {
        throw e
      }
    }
    throw e
  }
}
